<script setup lang="ts">
// Shared Cmd/Ctrl-K command palette for both apps. Built on Reka's Dialog (so
// it carries focus-trap / scroll-lock / Escape / aria for free) wrapping a Reka
// Listbox: the ListboxFilter is the search input - it forwards Up/Down/Home/End
// + Enter to the listbox and auto-highlights the first row on type, so keyboard
// nav + select are handled by the primitive. We own the FILTERING: fuse.js
// fuzzy-matches the static command groups; groups flagged `dynamic` (host
// already query-filtered, e.g. server history search) render verbatim.
//
// The host supplies a reactive `groups` array and (optionally) listens to
// `update:query` to drive dynamic groups. Selecting a row runs its `perform()`
// and closes. Opened from a global key handler (no DialogTrigger), so we
// prevent Reka's close-auto-focus - a perform() that navigates or opens another
// overlay owns focus afterward, and there's no trigger to hand focus back to.
import { computed, ref, watch } from 'vue';
import {
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  VisuallyHidden,
  ListboxRoot,
  ListboxFilter,
  ListboxContent,
  ListboxGroup,
  ListboxGroupLabel,
  ListboxItem,
} from 'reka-ui';
import Fuse from 'fuse.js';
import { useIcons } from '../icons';
import type { CommandGroup, CommandItem } from '../lib/commands';

const props = withDefaults(defineProps<{
  open: boolean;
  groups: CommandGroup[];
  placeholder?: string | undefined;
  emptyText?: string | undefined;
}>(), {
  placeholder: 'Type a command or search…',
  emptyText: 'No matches.',
});

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'update:query', value: string): void;
}>();

const icons = useIcons();
const query = ref('');

// Reset the filter each time the palette opens so a stale query never greets
// the user; emit it so host-driven dynamic groups reset in step.
watch(() => props.open, (isOpen) => {
  if (isOpen) { query.value = ''; emit('update:query', ''); }
});
watch(query, (q) => emit('update:query', q));

// One Fuse index over the STATIC items, rebuilt when the static groups change.
// label/keywords/hint are all indexed so an agent name parked in `keywords` or
// a value in `hint` still matches. Dynamic groups are excluded (host-filtered).
interface Indexed { gid: string; item: CommandItem }
const staticIndex = computed<Indexed[]>(() =>
  props.groups
    .filter((g) => !g.dynamic)
    .flatMap((g) => g.items.map((item) => ({ gid: g.id, item }))),
);
const fuse = computed(() => new Fuse(staticIndex.value, {
  keys: [
    { name: 'item.label', weight: 0.7 },
    { name: 'item.keywords', weight: 0.2 },
    { name: 'item.hint', weight: 0.1 },
  ],
  threshold: 0.4,
  ignoreLocation: true,
}));

// Matched static-item values for the current query (null = no query => all).
const matched = computed<Set<string> | null>(() => {
  const q = query.value.trim();
  if (!q) return null;
  return new Set(fuse.value.search(q).map((r) => `${r.item.gid}:${r.item.item.id}`));
});

// Groups to render, in declared order: dynamic groups pass through whole;
// static groups keep only fuzzy-matched items. Empty groups drop out. Each row
// gets a stable `_value` the Listbox uses for roving focus + select.
interface RenderItem extends CommandItem { _value: string }
interface RenderGroup { id: string; label?: string; items: RenderItem[] }
const rendered = computed<RenderGroup[]>(() => {
  const out: RenderGroup[] = [];
  const m = matched.value;
  for (const g of props.groups) {
    const items = g.items
      .filter((it) => g.dynamic || m === null || m.has(`${g.id}:${it.id}`))
      .map((it) => ({ ...it, _value: `${g.id}:${it.id}` }));
    if (items.length) out.push({ id: g.id, ...(g.label ? { label: g.label } : {}), items });
  }
  return out;
});

const isEmpty = computed(() => rendered.value.length === 0);

function onUpdateOpen(next: boolean): void {
  if (!next) emit('update:open', false);
}

async function onSelect(item: RenderItem): Promise<void> {
  if (item.disabled) return;
  emit('update:open', false);
  await item.perform();
}
</script>

<template>
  <DialogRoot :open="open" @update:open="onUpdateOpen">
    <DialogPortal>
      <DialogOverlay class="hq-cmdk__overlay" />
      <DialogContent
        class="hq-cmdk"
        :aria-describedby="undefined"
        @close-auto-focus="(e) => e.preventDefault()"
      >
        <VisuallyHidden as-child>
          <DialogTitle>Command palette</DialogTitle>
        </VisuallyHidden>

        <ListboxRoot class="hq-cmdk__listbox" highlight-on-hover>
          <div class="hq-cmdk__search">
            <component :is="icons.search" v-if="icons.search" :size="18" class="hq-cmdk__search-icon" />
            <ListboxFilter
              v-model="query"
              class="hq-cmdk__input"
              :placeholder="placeholder"
              :auto-focus="false"
              aria-label="Search commands"
            />
          </div>

          <ListboxContent class="hq-cmdk__list">
            <p v-if="isEmpty" class="hq-cmdk__empty">{{ emptyText }}</p>
            <ListboxGroup
              v-for="g in rendered"
              :key="g.id"
              class="hq-cmdk__group"
            >
              <ListboxGroupLabel v-if="g.label" class="hq-cmdk__grouplabel">
                {{ g.label }}
              </ListboxGroupLabel>
              <ListboxItem
                v-for="item in g.items"
                :key="item._value"
                :value="item._value"
                :disabled="!!item.disabled"
                class="hq-cmdk__item"
                @select="onSelect(item)"
              >
                <component
                  :is="item.icon"
                  v-if="item.icon"
                  :size="17"
                  class="hq-cmdk__item-icon"
                />
                <span class="hq-cmdk__item-label">{{ item.label }}</span>
                <span v-if="item.hint" class="hq-cmdk__item-hint">{{ item.hint }}</span>
                <span v-if="item.shortcut && item.shortcut.length" class="hq-cmdk__item-keys">
                  <kbd v-for="(k, i) in item.shortcut" :key="i" class="hq-cmdk__kbd">{{ k }}</kbd>
                </span>
              </ListboxItem>
            </ListboxGroup>
          </ListboxContent>

          <footer class="hq-cmdk__foot">
            <span><kbd class="hq-cmdk__kbd">↑</kbd><kbd class="hq-cmdk__kbd">↓</kbd> navigate</span>
            <span><kbd class="hq-cmdk__kbd">↵</kbd> select</span>
            <span><kbd class="hq-cmdk__kbd">esc</kbd> close</span>
          </footer>
        </ListboxRoot>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<!-- Unscoped: Reka portals the overlay + content to <body>, so a scoped style
     hash would never reach them. Classes are `hq-cmdk`-prefixed; design tokens
     resolve off :root in both apps. data-state hooks are Reka's. -->
<style>
.hq-cmdk__overlay {
  position: fixed;
  inset: 0;
  background: var(--scrim);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 230;
}
.hq-cmdk__overlay[data-state="open"] { animation: hq-cmdk-fade var(--dur-2, 0.18s) var(--ease-out, ease); }
.hq-cmdk__overlay[data-state="closed"] { animation: hq-cmdk-fade var(--dur-2, 0.18s) var(--ease-out, ease) reverse; }

/* Pinned near the top (command-palette convention), not vertically centered. */
.hq-cmdk {
  position: fixed;
  left: 50%;
  top: 12vh;
  transform: translateX(-50%);
  z-index: 231;
  box-sizing: border-box;
  width: min(620px, 94vw);
  max-height: min(64vh, 560px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--surface-panel);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-overlay);
}
.hq-cmdk[data-state="open"] { animation: hq-cmdk-in var(--dur-3, 0.22s) var(--ease-out, ease); }
.hq-cmdk[data-state="closed"] { animation: hq-cmdk-in var(--dur-2, 0.18s) var(--ease-out, ease) reverse; }

.hq-cmdk__listbox {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* Search row - the filter input + leading magnifier. */
.hq-cmdk__search {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
}
.hq-cmdk__search-icon { flex-shrink: 0; color: var(--ink-subtle); }
.hq-cmdk__input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: var(--ink-display);
  font: inherit;
  font-size: var(--text-md);
  line-height: 1.5;
  outline: none;
}
.hq-cmdk__input::placeholder { color: var(--ink-subtle); }

/* Scrollable result list. */
.hq-cmdk__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-2);
}
.hq-cmdk__empty {
  margin: 0;
  padding: var(--space-5) var(--space-3);
  text-align: center;
  font-size: var(--text-sm);
  color: var(--ink-subtle);
}
.hq-cmdk__group { display: block; }
.hq-cmdk__group + .hq-cmdk__group { margin-top: var(--space-2); }
.hq-cmdk__grouplabel {
  display: block;
  padding: var(--space-2) var(--space-3) var(--space-1);
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
  color: var(--ink-subtle);
}

.hq-cmdk__item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-input);
  font-size: var(--text-sm);
  color: var(--ink-primary);
  cursor: pointer;
  user-select: none;
  outline: none;
}
/* Reka sets data-highlighted on the roving-focused row (keyboard + hover). */
.hq-cmdk__item[data-highlighted] {
  background: var(--accent-default);
  color: var(--accent-text);
}
.hq-cmdk__item[data-disabled] { opacity: 0.45; cursor: default; }
.hq-cmdk__item-icon { flex-shrink: 0; color: var(--ink-subtle); }
.hq-cmdk__item[data-highlighted] .hq-cmdk__item-icon { color: var(--accent-text); }
.hq-cmdk__item-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hq-cmdk__item-hint {
  flex-shrink: 0;
  font-size: var(--text-xs);
  color: var(--ink-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 45%;
}
.hq-cmdk__item[data-highlighted] .hq-cmdk__item-hint { color: var(--accent-text); opacity: 0.85; }
.hq-cmdk__item-keys { flex-shrink: 0; display: inline-flex; gap: 3px; }

.hq-cmdk__kbd {
  font-family: var(--font-mono);
  font-size: 10px;
  line-height: 1.4;
  padding: 1px 5px;
  border-radius: var(--radius-chip);
  border: 1px solid var(--border-subtle);
  background: var(--surface-sunken);
  color: var(--ink-muted);
}
.hq-cmdk__item[data-highlighted] .hq-cmdk__kbd {
  border-color: transparent;
  background: color-mix(in srgb, var(--accent-text) 22%, transparent);
  color: var(--accent-text);
}

/* Footer key hints. */
.hq-cmdk__foot {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-2) var(--space-4);
  border-top: 1px solid var(--border-subtle);
  font-size: var(--text-xs);
  color: var(--ink-subtle);
}
.hq-cmdk__foot span { display: inline-flex; align-items: center; gap: 4px; }

@keyframes hq-cmdk-fade { from { opacity: 0; } }
@keyframes hq-cmdk-in {
  from { opacity: 0; transform: translate(-50%, -8px) scale(0.98); }
}
@media (prefers-reduced-motion: reduce) {
  .hq-cmdk[data-state],
  .hq-cmdk__overlay[data-state] { animation: none; }
}
</style>
